/**
 * useEventList — one fetch of the whole event list.
 *
 * `ListEvents` takes no filter parameters, so every surface that needs events needs the
 * same whole set and then narrows it in memory. That makes the fetch identical
 * everywhere, which is why it lives here instead of being written out once per page.
 *
 * Aborts on unmount and ignores a late response, so navigating away mid-flight cannot
 * set state on a gone component or overwrite the next page's data.
 */

import { useCallback, useEffect, useState } from 'react';
import { eventHubCalendarClient, type Event } from '../client/EventCalendarClient';

export interface EventListState {
  events: readonly Event[];
  loading: boolean;
  /** Error message, or null. Empty-vs-failed are different states for the UI. */
  error: string | null;
  /** Re-fetches — for after a create, or a retry. */
  reload: () => void;
}

/**
 * @param enabled Pass false to skip the fetch entirely. For a page that decides it has
 * nothing to show — a caller the service would refuse, say — a render-time early return
 * comes too late: the effect has already fired. Defaults to true.
 */
export function useEventList(enabled = true): EventListState {
  const [events, setEvents] = useState<readonly Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }
    const controller = new AbortController();
    let cancelled = false;
    const client = eventHubCalendarClient();

    setLoading(true);
    setError(null);

    client
      .listEvents({ signal: controller.signal })
      .then((result) => {
        if (cancelled) return;
        setEvents(result);
        setLoading(false);
      })
      .catch((e: unknown) => {
        if (cancelled || controller.signal.aborted) return;
        setEvents([]);
        setError(e instanceof Error ? e.message : String(e));
        setLoading(false);
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [nonce, enabled]);

  return { events, loading, error, reload };
}
